import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Policy } from "@/types";
import AnayaIcon from "@/assets/anaya.jpg";

const FreeServiceInteraction: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState<"question" | "confirmed">("question");

  // Retrieve policy data passed from the Policies page
  const policy = location.state?.policy as Policy;

  // If no policy data is found (e.g. direct URL access), go back
  useEffect(() => {
    if (!policy) {
      navigate("/customer/policies");
    }
  }, [policy, navigate]);

  if (!policy) return null;

  const handleNo = () => {
    // Instantly close/go back
    navigate("/customer/policies");
  };

  const handleYes = () => {
    setStep("confirmed");
  };

  return (
    <div className="tw-min-h-screen tw-bg-[#101010] tw-text-gray-100 tw-font-sans">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-p-4 tw-border-b tw-border-gray-800 tw-bg-[#101010] tw-sticky tw-top-0 tw-z-10">
        <button
          onClick={() => navigate(-1)}
          className="tw-mr-4 tw-p-2 hover:tw-bg-gray-800 tw-rounded-full tw-transition-colors"
        >
          <ArrowLeft className="tw-w-6 tw-h-6 tw-text-white" />
        </button>
        <span className="tw-w-full">
          <img src="/logo.png" alt="logo" className="tw-mx-auto" />
        </span>
      </div>

      {/* Chat Container */}
      <div className="tw-max-w-3xl tw-mx-auto tw-p-4 tw-space-y-6 tw-mt-4">
        {/* Assistant Message 1: Eligibility & Features */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="tw-flex tw-gap-4 tw-items-start"
        >
          <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-200 tw-overflow-hidden tw-flex-shrink-0">
            {/* Placeholder Avatar */}
            <img
              src={AnayaIcon}
              alt="Assistant"
              className="tw-w-full tw-h-full tw-object-cover"
            />
          </div>
          <div className="tw-bg-[#2C2C2E] tw-p-5 tw-rounded-2xl tw-rounded-tl-none tw-text-sm tw-leading-relaxed tw-text-gray-200 tw-max-w-[90%]">
            <p className="tw-mb-2">
              Great! You’re eligible for Free Annual Professional Care
            </p>
            <p className="tw-mb-2">
              This service helps maintain your{" "}
              <span className="tw-font-semibold tw-text-white">
                {policy.productName} (ID: {policy.id})
              </span>{" "}
              in top condition — ensuring better performance, safety &
              reliability.
            </p>
            <p className="tw-mb-2">Here’s what your free service includes:</p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-4 tw-text-gray-300">
              <li>Full performance check</li>
              <li>Safety inspection</li>
              <li>Professional cleaning</li>
              <li>Internal & external component review</li>
            </ul>
            <p className="tw-mb-1">Before I guide you, one quick thing:</p>
            <p className="tw-font-medium tw-text-white">
              Is your product currently functional?
            </p>
          </div>
        </motion.div>

        {/* Interaction Area */}
        {step === "question" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tw-flex tw-justify-end tw-gap-3 tw-mt-4"
          >
            <button
              onClick={handleNo}
              className="tw-px-6 tw-py-2 tw-rounded-full tw-border tw-border-gray-600 tw-text-gray-300 hover:tw-bg-gray-800 tw-transition-colors"
            >
              No
            </button>
            <button
              onClick={handleYes}
              className="tw-px-6 tw-py-2 tw-rounded-full tw-bg-[#00C5FB] tw-text-gray-900 tw-font-semibold hover:tw-bg-cyan-500 tw-transition-colors"
            >
              Yes
            </button>
          </motion.div>
        )}

        {/* Confirmation Step */}
        {step === "confirmed" && (
          <>
            {/* User Response Bubble */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="tw-flex tw-justify-end"
            >
              <div className="tw-bg-[#3A3A3C] tw-px-5 tw-py-3 tw-rounded-2xl tw-rounded-tr-none tw-text-white">
                Yes
              </div>
            </motion.div>

            {/* Assistant Response 2: Success */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="tw-flex tw-gap-4 tw-items-start"
            >
              <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-200 tw-overflow-hidden tw-flex-shrink-0">
                <img
                  src={AnayaIcon}
                  alt="Assistant"
                  className="tw-w-full tw-h-full tw-object-cover"
                />
              </div>
              <div className="tw-bg-[#2C2C2E] tw-p-5 tw-rounded-2xl tw-rounded-tl-none tw-text-sm tw-leading-relaxed tw-text-gray-200 tw-max-w-[90%]">
                <p className="tw-mb-2">
                  Perfect! Since your{" "}
                  <span className="tw-font-semibold tw-text-white">
                    {policy.productName}
                  </span>{" "}
                  is functional, you can directly schedule your free maintenance
                  service.
                </p>
                <p className="tw-mb-1">Please contact our Service Team at:</p>
                <p className="tw-flex tw-items-center tw-text-white tw-font-semibold tw-mb-2">
                  📞 +91-22-69620766
                </p>
                <p>They’ll help you book your pickup and service slot.</p>
                <p>Let me know if you need help with anything else!</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="tw-flex tw-justify-end"
            >
              <button
                className="tw-px-6 tw-py-2 tw-rounded-full tw-bg-[#00C5FB] tw-text-gray-900 tw-font-semibold hover:tw-bg-cyan-500 tw-transition-colors"
                onClick={() => {
                  navigate("/customer/policies");
                }}
              >
                Close
              </button>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default FreeServiceInteraction;
